import { useEffect, useState } from 'react';
import { TyreAPI } from '../api/tyreApi';
import { TyreDto } from '../api/tyreApi';
import api from '../api/axiosInstance';

function AdminTyresPage() {
  const [tyres, setTyres] = useState<TyreDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadType, setUploadType] = useState<'csv' | 'xlsx'>('csv');

  const loadTyres = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await TyreAPI.getTyresWithFilter(
        {},
        { page, pageSize }
      );
      setTyres(response.data);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
    } catch (e: any) {
      setError(e?.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTyres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const endpoint = uploadType === 'csv' ? '/tyres/csv' : '/tyres/xlsx';
      await api.post(endpoint, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Файл успешно загружен');
      setShowUploadModal(false);
      setUploadFile(null);
      await loadTyres();
    } catch (e: any) {
      alert(e?.response?.data?.message || e?.message || 'Ошибка загрузки файла');
    } finally {
      setUploading(false);
    }
  };

  const handleToggleActive = async (tyre: TyreDto) => {
    try {
      // Обновляем статус через API (нужно будет добавить эндпоинт)
      // Пока просто обновляем локально
      setTyres(tyres.map(t => 
        t.id === tyre.id ? { ...t, isActive: !t.isActive } : t
      ));
    } catch (e: any) {
      alert(e?.message || 'Ошибка обновления');
    }
  };

  const filteredTyres = search
    ? tyres.filter(t => 
        t.name?.toLowerCase().includes(search.toLowerCase()) ||
        t.brand?.toLowerCase().includes(search.toLowerCase()) ||
        t.uniqueSKU?.toLowerCase().includes(search.toLowerCase()) ||
        t.vendorSKU?.toLowerCase().includes(search.toLowerCase())
      )
    : tyres;

  return (
    <div className="flex-1 p-4 md:p-6">
      <div className="flex flex-col gap-4">
        <h1 className="text-xl font-semibold">Шины</h1>

        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex flex-col">
            <label className="text-sm text-gray-500">Поиск</label>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="border rounded px-2 py-1"
              placeholder="Название / Бренд / SKU"
            />
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="ml-auto bg-black text-white rounded px-3 py-2"
          >
            Загрузить из файла
          </button>
        </div>

        <div className="border rounded">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">ID</th>
                  <th className="px-3 py-2 text-left">Изображение</th>
                  <th className="px-3 py-2 text-left">Бренд</th>
                  <th className="px-3 py-2 text-left">Название</th>
                  <th className="px-3 py-2 text-left">Размер</th>
                  <th className="px-3 py-2 text-left">Цена</th>
                  <th className="px-3 py-2 text-left">Остаток</th>
                  <th className="px-3 py-2 text-left">Активен</th>
                  <th className="px-3 py-2 text-left">Действия</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td className="px-3 py-4" colSpan={9}>
                      Загрузка...
                    </td>
                  </tr>
                )}
                {error && !loading && (
                  <tr>
                    <td className="px-3 py-4 text-red-600" colSpan={9}>
                      {error}
                    </td>
                  </tr>
                )}
                {!loading && !error && filteredTyres.length === 0 && (
                  <tr>
                    <td className="px-3 py-4" colSpan={9}>
                      Шины не найдены
                    </td>
                  </tr>
                )}
                {!loading &&
                  !error &&
                  filteredTyres.map(tyre => (
                    <tr key={tyre.id} className="border-t">
                      <td className="px-3 py-2">{tyre.id}</td>
                      <td className="px-3 py-2">
                        {tyre.image ? (
                          <img
                            src={tyre.image}
                            alt=""
                            className="w-16 h-16 object-contain bg-white border rounded"
                            onError={e => {
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 border rounded flex items-center justify-center text-xs text-gray-400">
                            Нет фото
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">{tyre.brand}</td>
                      <td className="px-3 py-2">{tyre.name}</td>
                      <td className="px-3 py-2">
                        {tyre.width}/{tyre.profile} R{tyre.diameter}
                      </td>
                      <td className="px-3 py-2">{tyre.price.toFixed(2)} ₽</td>
                      <td className="px-3 py-2">{tyre.residue}</td>
                      <td className="px-3 py-2">
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={tyre.isActive}
                            onChange={() => handleToggleActive(tyre)}
                          />
                          {tyre.isActive ? 'Да' : 'Нет'}
                        </label>
                      </td>
                      <td className="px-3 py-2">
                        <button
                          className="px-2 py-1 border rounded text-xs"
                          onClick={() => {
                            // Можно добавить редактирование
                            alert('Редактирование в разработке');
                          }}
                        >
                          Редактировать
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 p-3 border-t">
            <button
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-2 py-1 border rounded"
            >
              Назад
            </button>
            <div>
              Стр. {page} из {totalPages} (Всего: {totalCount})
            </div>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(p => p + 1)}
              className="px-2 py-1 border rounded"
            >
              Вперед
            </button>
            <div className="ml-auto flex items-center gap-2">
              <span>На странице</span>
              <select
                value={pageSize}
                onChange={e => {
                  setPage(1);
                  setPageSize(Number(e.target.value));
                }}
                className="border rounded px-2 py-1"
              >
                {[10, 20, 50, 100].map(s => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {showUploadModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white w-full max-w-md border rounded p-4 flex flex-col gap-3">
              <h2 className="font-medium">Загрузка шин из файла</h2>
              <div className="flex flex-col gap-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={uploadType === 'csv'}
                    onChange={() => setUploadType('csv')}
                  />
                  CSV
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={uploadType === 'xlsx'}
                    onChange={() => setUploadType('xlsx')}
                  />
                  XLSX
                </label>
              </div>
              <label className="flex flex-col">
                <span className="text-sm text-gray-500">Файл</span>
                <input
                  type="file"
                  accept={uploadType === 'csv' ? '.csv' : '.xlsx,.xls'}
                  onChange={e => setUploadFile(e.target.files?.[0] ?? null)}
                />
              </label>
              <div className="flex gap-2 justify-end">
                <button
                  className="bg-black text-white rounded px-3 py-2 disabled:opacity-50"
                  onClick={handleUpload}
                  disabled={!uploadFile || uploading}
                >
                  {uploading ? 'Загрузка...' : 'Загрузить'}
                </button>
                <button
                  className="border rounded px-3 py-2"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                  }}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminTyresPage;

